import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BarChart3, Circle, CircleDot, FileText, TrendingUp, DollarSign } from "lucide-react";

interface CostConfigurationTabProps {
  costWeight: number;
  useModeledCost: boolean;
  modeledCostPercent: number | undefined;
  onUseModeledChange: (useModeled: boolean) => void;
  onModeledPercentChange: (percent: number | undefined) => void;
}

export function CostConfigurationTab({
  costWeight,
  useModeledCost,
  modeledCostPercent,
  onUseModeledChange,
  onModeledPercentChange
}: CostConfigurationTabProps) {
  // When Cost is reweighted to 0, show percentage out of 100 instead of weighted score
  const isReweighted = costWeight === 0;
  const effectiveMax = isReweighted ? 100 : costWeight;
  
  const currentScore = useModeledCost 
    ? (modeledCostPercent !== undefined 
        ? (isReweighted ? modeledCostPercent : (modeledCostPercent * costWeight / 100)) 
        : 0)
    : 0; // Calculated score from reports not yet implemented
  const progressPercent = useModeledCost
    ? (modeledCostPercent || 0)
    : 0;

  // Circle dimensions for SVG (matching PI/IA)
  const strokeWidth = 5;

  // Circular progress component (matching PI/IA design exactly)
  const CircularProgress = ({ sz = 100 }: { sz?: number }) => {
    const r = (sz - strokeWidth) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (progressPercent / 100) * c;
    return (
      <div className="relative" style={{ width: sz, height: sz }}>
        <svg className="transform -rotate-90" width={sz} height={sz}>
          <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
          <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke="hsl(var(--primary))" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold text-gray-900">{Math.round(currentScore)}</span>
          <span className="text-xs text-gray-500">of {effectiveMax}{isReweighted ? '%' : ''}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Cost Score Method Box */}
      <Card>
        <CardContent className="py-5 px-6">
          <div className="flex items-center">
            <div className="flex flex-col pr-6 border-r border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Score Method</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => onUseModeledChange(false)} 
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all w-full ${!useModeledCost ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {!useModeledCost ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  Calculate from Cost reports
                </button>
                <button 
                  onClick={() => onUseModeledChange(true)} 
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all w-full ${useModeledCost ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {useModeledCost ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  Enter estimate
                </button>
                {useModeledCost && (
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      min="0" 
                      max="100" 
                      placeholder="0-100" 
                      value={modeledCostPercent || ''} 
                      onChange={(e) => onModeledPercentChange(e.target.value ? parseFloat(e.target.value) : undefined)} 
                      className="w-[92px] h-9" 
                    />
                    <span className="text-sm text-muted-foreground">% expected Cost score</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 px-6">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Cost Score Preview
              </h3>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                Cost contributes <strong className="text-gray-900">{costWeight}%</strong> to your final MIPS score
                {isReweighted && <span className="text-gray-500"> (reweighted)</span>}
              </p>
              <p className="text-sm text-amber-800 mt-2.5 leading-relaxed">
                {useModeledCost 
                  ? <>Using estimated score. Cost Feedback Reports feature coming soon.</>
                  : <>Score will be calculated from <strong>Cost Feedback Reports</strong> (coming soon).</>
                }
              </p>
            </div>
            <div className="flex-shrink-0 pl-6 border-l border-gray-200">
              {/* Show dash when reweighted, otherwise show score display or circular progress */}
              {isReweighted ? (
                <div className="text-center min-w-[100px]">
                  <div className="text-4xl font-bold text-gray-400">—</div>
                  <div className="text-sm text-gray-500 mt-1">Reweighted</div>
                </div>
              ) : !useModeledCost ? (
                <div className="text-center min-w-[100px]">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">
                    {Math.round(currentScore)}<span className="text-2xl text-gray-400 font-normal">/{costWeight}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">Weighted Score</div>
                </div>
              ) : (
                <CircularProgress />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Performance Info Card */}
      <Card className="bg-gray-50/50">
        <CardHeader className="pb-3">
          <CardTitle>Understanding the Cost Category</CardTitle>
          <CardDescription>How CMS calculates your Cost performance score</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600">
            <p>
              The Cost category is <strong className="text-gray-700">calculated entirely by CMS</strong> based on Medicare 
              Part B claims data for your attributed patients. Unlike other MIPS categories, you do not submit data for Cost—CMS 
              automatically evaluates your practice's resource use and compares it to national and specialty-specific benchmarks.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                <DollarSign className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-700">How Cost is Measured</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    CMS calculates Cost using Total Per Capita Cost (TPCC) and Medicare Spending Per Beneficiary (MSPB) measures. 
                    These are derived from Part B claims for patients attributed to your practice based on primary care services.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-700">Cost Feedback Reports</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    CMS provides quarterly Cost Feedback Reports through the QPP portal. These reports help you track 
                    your historical performance and estimate your Cost score before final results are published.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                <TrendingUp className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-700">Performance Benchmarks</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Your Cost score reflects how efficiently you deliver care compared to peers. Lower costs relative to benchmarks 
                    result in higher scores. The national median typically scores around 50 points.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </CardContent>
      </Card>
    </div>
  );
}
